import { BarElement, CategoryScale, Chart as ChartJS, Legend, LinearScale, Title, Tooltip } from 'chart.js';
import React from 'react';
import { DashBoardDonutCard, IDashboardDonutChartDataSet } from '../../components/UI-Elements/DashBoardDonutCard';
import { StackedBarChartElement } from '../../sp-types/dashboardTypes';
import { formatToShortString } from '../../utils/FormatValue';
import { GENERAL_ID } from '../../components/Stores/exposureDataStore';
import { getUnitObj } from '../../utils/MassUnits/massUnitsUtils';
import { cumulaticeChips, MULTIPLE_MATS, NO_DATA, terminalChips, valChips } from './dashboardUtils';
import { ToggleChips } from './dashboard-components/ToggleChips';
import { StackedBarChart } from './StackedBarDoughnutChartCard';
import { useMaterialsStore } from '../../components/Stores/MaterialsStore';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

interface Props {
  monthExposures: StackedBarChartElement[];
  cumulativeExposures: StackedBarChartElement[];
  cardTitle: string;
  cardDescription: string;
  cardUrl: string;
  materialId: string;
  currentDate?: Date;
  addNewUrl?: string;
  terminals: string;
  isTerminals: boolean;
  setIsTerminals: (_isTerminals: boolean) => void;
  isQuantity: boolean;
  setIsQuantity: (_isQuantity: boolean) => void;
}

const emptyMockFutureOrdersData: StackedBarChartElement[] = [
  {
    materialId: '',
    color: '#EAEAEB',
    fieldName: 'No material',
    valueArray: [],
  },
];

const emptyMockFinancialPositionsData: StackedBarChartElement[] = [
  {
    materialId: '',
    color: '#EAEAEB',
    fieldName: 'No material',
    valueArray: [],
  },
];

function sumValueArrayValues(elements: StackedBarChartElement[]): number {
  let totalSum = 0;

  // Iterate over each StackedBarChartElement
  elements.forEach((element) => {
    // Iterate over each value in the valueArray of the current element
    element.valueArray.forEach((valueObject) => {
      // Add the value to the total sum
      totalSum += valueObject.value;
    });
  });

  return totalSum;
}

export const StackedBarChartDetails: React.FC<Props> = ({
  cardTitle,
  monthExposures,
  cumulativeExposures,
  currentDate = new Date(),
  materialId,
  isTerminals,
  setIsTerminals,
  terminals,
  isQuantity,
  setIsQuantity,
}) => {
  const { materialsMap } = useMaterialsStore();
  const [isCumulative, setIsCumulative] = React.useState(true);

  // Function to convert a single StackedBarChartElement to IDashboardDonutChartDataSet
  function convertToDonutChartDataSet(element: StackedBarChartElement, currentDate: Date): IDashboardDonutChartDataSet {
    const currentMonth = currentDate.getMonth() + 1;
    const currentYear = currentDate.getFullYear();
    const value =
      element.valueArray.find((item) => item.date.month === currentMonth && item.date.year === currentYear)?.value || 0;

    return {
      fieldName: element.fieldName,
      color: element.color,
      value: value,
      unit: element.unit,
      price: element.price,
      ratio: 0,
    };
  }

  // Function to convert an array of StackedBarChartElement to an array of IDashboardDonutChartDataSet
  function convertArrayToDonutChartDataSetArray(
    elements: StackedBarChartElement[],
    currentDate: Date,
  ): IDashboardDonutChartDataSet[] {
    return elements.map((element) => convertToDonutChartDataSet(element, currentDate));
  }

  const donutChartData = convertArrayToDonutChartDataSetArray(cumulativeExposures, currentDate).map((item) => ({
    ...item,
    origVal: item.value,
    value: isQuantity ? item.value * (item.price || 1) : item.value,
  }));

  const materialExposures = donutChartData.filter((a) => !!a.fieldName);
  const unitSum = materialExposures.reduce((sum, item) => sum + item.value / (item.price || 1), 0);
  const secondaryTitle =
    unitSum > 0
      ? materialId === GENERAL_ID
        ? MULTIPLE_MATS
        : `${formatToShortString(unitSum)} ${getUnitObj(materialExposures?.[0].unit).shortForm}`
      : NO_DATA;

  const showData = isCumulative ? cumulativeExposures : monthExposures;
  const currentBarData =
    sumValueArrayValues(showData) !== 0
      ? showData
      : cardTitle == 'Financial positions'
      ? emptyMockFinancialPositionsData
      : emptyMockFutureOrdersData;

  return (
    <div className="flex flex-col h-full min-w-[40rem] bg-white p-3 overflow-auto ">
      <div className="flex justify-between">
        <h4 className="text-lg">{cardTitle}</h4>
      </div>
      <div className="flex flex-col flex-1 justify-between pt-4">
        <div className="flex mx-auto w-[22vh]   justify-center ">
          <DashBoardDonutCard
            dataSet={donutChartData}
            secondaryTitle={secondaryTitle}
            ratioLine=""
            cssSize="w-52 h-52"
          />
        </div>
        <div className="flex h-16 items-center gap-2 text-sm">
          <p>View by:</p>
          {materialId !== GENERAL_ID && (
            <ToggleChips
              width="5rem"
              selected={isQuantity ? 1 : 0}
              setSelected={(isQuan) => setIsQuantity(isQuan === 1)}
              chips={valChips}
            />
          )}
          {!!terminals && (
            <ToggleChips
              width="5rem"
              selected={isTerminals ? 1 : 0}
              setSelected={(isTerm) => setIsTerminals(isTerm === 1)}
              chips={terminalChips}
            />
          )}
          <ToggleChips
            width="6rem"
            selected={isCumulative ? 1 : 0}
            setSelected={(isCumulative) => setIsCumulative(isCumulative === 1)}
            chips={cumulaticeChips}
          />
        </div>
        <div className="h-[12.5rem]">
          <StackedBarChart
            stackedBarChartData={currentBarData}
            terminals={isTerminals ? terminals : ''}
            isCumulative={isCumulative}
            setMaxVal={console.log}
            chartMax={0}
            showProportion={false}
            name="financial-positions"
            unit={
              materialId !== GENERAL_ID && isQuantity ? getUnitObj(materialsMap[materialId]?.unit).shortForm + ' ' : '$'
            }
          />
        </div>
      </div>
    </div>
  );
};
